<template>
  <div
    v-if="!fetchingDrm && ready"
    class="drm d-flex flex-column justify-content-between h-100"
  >
    <b-row class="justify-content-center">
      <b-col cols="12">
        <drm-type
          :value="useWideVine"
          no-edit
          title="Wide Vine Encryption"
          description="Distribute securely and protect playback of content on any consumer device with Widevine DRM solution"
        >
          <wide-vine :settings="settings.widevineEncryption" />
        </drm-type>
        <drm-type
          :value="useRawKeyEncryption"
          title="Raw Key Encryption"
          :has-padding="false"
          no-edit
          description="Raw Keys are used when you are managing the encryption keys, for which keys and key_ids are provided directly, it also supports multi-DRM by providing custom PSSHs"
        >
          <raw-key :settings="settings.rawKeyEncryption" />
        </drm-type>
        <drm-type
          :value="usePlayReadyEncryption"
          title="PlayReady Encryption"
          no-edit
          description="Microsoft PlayReady provides content encryption for all platforms and devices"
        >
          <play-ready :settings="settings.playReadyEncryption" />
        </drm-type>
        <drm-type
          :value="useEzDrm"
          title="EZ DRM"
          no-edit
          description="EZ DRM offers a wide range of options, including multi-DRM solution and wide-range of options in DRMaaS"
        >
          <ez-drm :settings="settings.ezDRM" />
        </drm-type>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import DrmType from './DrmType.vue'
import WideVine from './helpers/WideVine.vue'
import RawKey from './helpers/RawKey.vue'
import PlayReady from './helpers/PlayReady.vue'
import EzDrm from './helpers/EzDrm.vue'

const emptySettings = {
  widevineEncryption: {
    keyServerUrl: '',
    signer: '',
    aesSigningKey: '',
    aesSigningIV: '',
    rsaSigningKeyPath: '',
  },
  rawKeyEncryption: {
    iv: '',
    pssh: '',
    rawKeys: [
      {
        id: uuidv4(),
        label: '',
        keyId: '',
        key: '',
      },
    ],
  },
  playReadyEncryption: {
    playReadyServerUrl: '',
    clientCertFile: '',
    clientCertPrivateKeyFile: '',
    clientCertPrivateKeyPassword: '',
  },
  ezDRM: {
    username: '',
    password: '',
  },
}

export default {
  name: 'DRMForm',
  components: {
    DrmType,
    WideVine,
    RawKey,
    PlayReady,
    EzDrm,
  },
  props: {
    drmSettings: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      ready: false,
      useWideVine: false,
      useRawKeyEncryption: false,
      usePlayReadyEncryption: false,
      useEzDrm: false,
      settings: {
        widevineEncryption: {
          keyServerUrl: '',
          signer: '',
          aesSigningKey: '',
          aesSigningIV: '',
          rsaSigningKeyPath: '',
        },
        rawKeyEncryption: {
          iv: '',
          pssh: '',
          rawKeys: [
            {
              id: uuidv4(),
              label: '',
              keyId: '',
              key: '',
            },
          ],
        },
        playReadyEncryption: {
          playReadyServerUrl: '',
          clientCertFile: '',
          clientCertPrivateKeyFile: '',
          clientCertPrivateKeyPassword: '',
        },
        ezDRM: {
          username: '',
          password: '',
        },
      },
    }
  },
  computed: {
    savingDrm() {
      return false
    },
    fetchingDrm() {
      return this.$store.state.drm.fetchingDrm
    },
  },
  created() {
    this.settings = { ...this.drmSettings }
    this.nullSettingsFix()
    this.initUseEzDrm()
    this.initUseWideVine()
    this.initUsePlayReadyEncryption()
    this.initUseRawKeyEncryption()
    this.ready = true
  },
  methods: {
    nullSettingsFix() {
      if (this.settings.playReadyEncryption === null) {
        this.settings.playReadyEncryption = emptySettings.playReadyEncryption
      }
      if (this.settings.ezDRM === null) {
        this.settings.ezDRM = emptySettings.ezDRM
      }
      if (this.settings.rawKeyEncryption === null) {
        this.settings.rawKeyEncryption = emptySettings.rawKeyEncryption
      }
      if (this.settings.widevineEncryption === null) {
        this.settings.widevineEncryption = emptySettings.widevineEncryption
      }
    },
    initUseEzDrm() {
      this.useEzDrm = !!(
        this.settings.ezDRM.username || this.settings.ezDRM.password
      )
    },
    initUseWideVine() {
      this.useWideVine = !!(
        this.settings.widevineEncryption.keyServerUrl
        && this.settings.widevineEncryption.signer
      )
    },
    initUsePlayReadyEncryption() {
      this.usePlayReadyEncryption = !!this.settings.playReadyEncryption
        .playReadyServerUrl
    },
    initUseRawKeyEncryption() {
      this.useRawKeyEncryption = !!(
        this.settings.rawKeyEncryption.iv && this.settings.rawKeyEncryption.pssh
      )
    },
    saveDrm() {
      this.$emit('submit', {
        widevineEncryption: this.useWideVine
          ? this.settings.widevineEncryption
          : emptySettings.widevineEncryption,
        rawKeyEncryption: this.useRawKeyEncryption
          ? this.settings.rawKeyEncryption
          : emptySettings.rawKeyEncryption,
        playReadyEncryption: this.usePlayReadyEncryption
          ? this.settings.playReadyEncryption
          : emptySettings.playReadyEncryption,
        ezDRM: this.useEzDrm ? this.settings.ezDRM : emptySettings.ezDRM,
      })
    },
  },
}
</script>
